import { Flex } from '@chakra-ui/react';
import { CheckMarkIcon, DoubleCheckMarkIcon, DownloadIcon, EmptyStateImage, TrashcanIcon } from '@assets/icons';
import { useEditDetail } from '../hooks/state/useEditDetail';
import { useConfirmModal } from '@store/useConfirmModal';
import useMutateCPList from '../hooks/mutate/useMutateCPList';
import { useSnackbarNotification } from '@store/snackbarNotification';
import { fileDownloader } from '@helpers/utils';

export const getButtonConfig = ({ role, statusKey, data, onDelete, onRequestApproval, onApprove, onReject }) => {
    const stateEdit = useEditDetail();
    const stateConfirm = useConfirmModal();
    const { claimPromoAllCust, completePromoAllCust } = useMutateCPList();
    const { onChangeSnackbarNotification } = useSnackbarNotification();

    const customer = {
        left: {
            default: {
                label: 'Hapus',
                icon: <TrashcanIcon color="#005B99" />,
                onClick: onDelete,
                variant: 'outline',
                hide: ['processing', 'done', 'active', 'ready to claim'].includes(statusKey),
            },
            processing: {
                label: (
                    <Flex gap="8px" alignItems="center">
                        <DownloadIcon /> Data Claim
                    </Flex>
                ),
                variant: 'outline',
                onClick: async () => {
                    stateEdit.set({
                        claimPromo: {
                            idPromo: stateEdit?.id,
                            isCancel: false,
                        },
                    });

                    try {
                        await fileDownloader({
                            url: `/api/v1/promotion-claim/${stateEdit?.id}/claim/export`,
                            fileName: 'Data Claim All Promotion.zip',
                        });

                        onChangeSnackbarNotification({
                            status: 'success',
                            description: 'Data Claim All didownload!',
                            duration: 3,
                        });
                    } catch (error) {
                        if (error.response && error.response.data instanceof Blob) {
                            const text = await error.response.data.text();
                            try {
                                const json = JSON.parse(text);
                                onChangeSnackbarNotification({
                                    status: 'error',
                                    title: 'Gagal download data!',
                                    description: `${json?.message}`,
                                    duration: 3,
                                });
                            } catch {
                                console.error('Error (not JSON):', text);
                            }
                        } else {
                            onChangeSnackbarNotification({
                                status: 'error',
                                description: 'Data Claim All gagal didownload!',
                                duration: 3,
                            });
                        }
                    } finally {
                        stateEdit.set({
                            claimPromo: null,
                        });
                    }
                },
            },
        },
        right: {
            draft: {
                label: 'Request Approval',
                onClick: onRequestApproval,
                disabled: data?.total_sku === 0 || data?.total_customer === 0,
            },
            done: {
                label: (
                    <Flex gap="8px" alignItems="center">
                        <DownloadIcon /> Data Claim
                    </Flex>
                ),
                variant: 'outline',
                onClick: async () => {
                    stateEdit.set({
                        claimPromo: {
                            idPromo: stateEdit?.id,
                            isCancel: false,
                        },
                    });

                    try {
                        await fileDownloader({
                            url: `/api/v1/promotion-claim/${stateEdit?.id}/claim/export`,
                            fileName: 'Data Claim All Promotion.zip',
                        });

                        onChangeSnackbarNotification({
                            status: 'success',
                            description: 'Data Claim All didownload!',
                            duration: 3,
                        });
                    } catch (error) {
                        if (error.response && error.response.data instanceof Blob) {
                            const text = await error.response.data.text();
                            try {
                                const json = JSON.parse(text);
                                onChangeSnackbarNotification({
                                    status: 'error',
                                    title: 'Gagal download data!',
                                    description: `${json?.message}`,
                                    duration: 3,
                                });
                            } catch {
                                console.error('Error (not JSON):', text);
                            }
                        } else {
                            onChangeSnackbarNotification({
                                status: 'error',
                                description: 'Data Claim All gagal didownload!',
                                duration: 3,
                            });
                        }
                    } finally {
                        stateEdit.set({
                            claimPromo: null,
                        });
                    }
                },
            },
            'ready to claim': {
                label: (
                    <Flex gap="8px" alignItems="center">
                        <CheckMarkIcon color="#fff" size={22} /> Claim All
                    </Flex>
                ),
                onClick: () => {
                    stateConfirm.set({
                        isOpen: true,
                        title: 'Claim All Promotion?',
                        desc: 'Claim dan selesaikan Consumer Promotion ini.',
                        btnLabelNo: 'Nanti',
                        btnLabelYes: 'Claim',
                        onSubmit: () => {
                            stateEdit.set({
                                claimPromo: {
                                    idPromo: stateEdit?.id,
                                },
                            });
                            claimPromoAllCust.mutate({ idPromo: stateEdit?.id });
                        },
                    });
                },
            },
            processing: {
                label: (
                    <Flex gap="8px" alignItems="center">
                        <DoubleCheckMarkIcon color="#fff" size={20} /> Complete All
                    </Flex>
                ),
                onClick: () => {
                    stateConfirm.set({
                        isOpen: true,
                        title: 'Complete All Process?',
                        desc: 'Pastikan jika proses pengiriman produk Non Jual telah dilakukan untuk semua customer.',
                        btnLabelNo: 'Nanti',
                        btnLabelYes: 'Complete',
                        ConfirmationStateImage: EmptyStateImage,
                        onSubmit: () => {
                            completePromoAllCust.mutate({ idPromo: stateEdit?.id });
                        },
                    });
                },
            },
            'waiting for approval': {
                label: 'Waiting for Approval',
                hide: ['active'].includes(statusKey),
            },
            reject: { label: 'Rejected', hide: true },
            planned: {
                label: (
                    <Flex gap="8px" alignItems="center">
                        <CheckMarkIcon color="#fff" size={22} /> Claim All
                    </Flex>
                ),
                disabled: true,
            },
            active: { label: 'Waiting for approval', disabled: true },
        },
    };

    const hideBtnAdm = ['planned', 'reject', 'done', 'active', 'ready to claim', 'draft'];

    const admin = {
        left: {
            label: 'Decline',
            onClick: onReject,
            variant: 'outline',
            hide: hideBtnAdm.includes(statusKey),
        },
        right: {
            label: 'Approve',
            onClick: onApprove,
            variant: 'solid',
            hide: hideBtnAdm.includes(statusKey),
        },
    };

    if (role === 'Area Leaders') {
        return admin;
    }

    const leftBtn = customer.left[statusKey] === 'processing' ? customer.left.processing : customer.left.default;
    const rightBtn = customer.right[statusKey] || {};

    return {
        left: leftBtn,
        right: rightBtn,
    };
};
